import type {
  BreakdownByType,
  HomeApiResponse,
  MetricsBySede,
  ProcessedHomeResult,
  RiskIndicators,
} from '@/domains/home/home.entity';
import type { Center, Day } from '@/domains/shared';
import { riskLevelColors, typesOfRisk } from '@/domains/shared';

/**
 * Use Case para agregar métricas de todos los años en una única estructura
 * Cumple SRP: Solo se encarga de sumar métricas agrupando por sede y jornada
 *
 * Responsabilidades:
 * - Agrupar métricas de múltiples años por combinación única sede+jornada
 * - Sumar todas las propiedades numéricas (estudiantes, riesgos, desgloses)
 * - Calcular totales globales de estudiantes y riesgos
 * - Retornar estructura ProcessedHomeResult con data agregada
 *
 * Caso de uso: Consolidar métricas históricas para visualización global
 */
export class AggregateMetricsAcrossYearsUseCase {
  /**
   * Crea una métrica vacía inicializada en 0 para una sede y jornada específicas
   */
  private createEmptyMetric(sede: Center, jornada: Day): MetricsBySede {
    const emptyDesglose: RiskIndicators = {} as RiskIndicators;

    for (const indicador of typesOfRisk) {
      const emptyBreakdown: BreakdownByType = {
        rojo: 0,
        amarillo: 0,
        verde: 0,
      };
      emptyDesglose[indicador] = emptyBreakdown;
    }

    return {
      sede,
      jornada,
      total_estudiantes: 0,
      riesgo_alto: 0,
      riesgo_medio: 0,
      riesgo_bajo: 0,
      desglose_por_tipo: emptyDesglose,
    };
  }

  /**
   * Suma las propiedades de una métrica origen a una métrica destino
   */
  private aggregateMetric(target: MetricsBySede, source: MetricsBySede): void {
    target.total_estudiantes += source.total_estudiantes;
    target.riesgo_alto += source.riesgo_alto;
    target.riesgo_medio += source.riesgo_medio;
    target.riesgo_bajo += source.riesgo_bajo;

    for (const indicador of typesOfRisk) {
      for (const color of riskLevelColors) {
        target.desglose_por_tipo[indicador][color] +=
          source.desglose_por_tipo[indicador][color];
      }
    }
  }

  /**
   * Agrega métricas de todos los años en una única estructura
   * @param apiResponse - Respuesta de API con datos de múltiples años (ya filtrada)
   * @returns Datos procesados con métricas agregadas por sede+jornada y resumen global
   * @throws Error si la respuesta es inválida o no tiene datos
   */
  execute(apiResponse: HomeApiResponse): ProcessedHomeResult {
    if (!apiResponse?.success) {
      throw new Error('La respuesta de la API indica un error');
    }

    if (!apiResponse.data || apiResponse.data.length === 0) {
      return {
        metricsBySede: [],
        metricsSummary: {
          total_estudiantes: 0,
          total_riesgo: 0,
        },
      };
    }

    const aggregatedMap = new Map<string, MetricsBySede>();

    for (const yearData of apiResponse.data) {
      if (!yearData.por_sede) continue;

      for (const sedeMetric of yearData.por_sede) {
        const key = `${sedeMetric.sede}-${sedeMetric.jornada}`;

        if (!aggregatedMap.has(key)) {
          aggregatedMap.set(
            key,
            this.createEmptyMetric(sedeMetric.sede, sedeMetric.jornada),
          );
        }

        const existingMetric = aggregatedMap.get(key)!;
        this.aggregateMetric(existingMetric, sedeMetric);
      }
    }

    const total_estudiantes = apiResponse.data.reduce(
      (sum, year) => sum + year.total_estudiantes,
      0,
    );

    const total_riesgo = apiResponse.data.reduce(
      (sum, year) => sum + year.total_riesgo,
      0,
    );

    return {
      metricsBySede: Array.from(aggregatedMap.values()),
      metricsSummary: {
        total_estudiantes,
        total_riesgo,
      },
    };
  }
}
